// PeopleStep.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Paper,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Alert,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  OutlinedInput,
  Chip
} from '@mui/material';
import STATES from '../constants/states';
import { isValidEmail, isValidZipCode, isValidDateOfBirth, isValidTaxId } from '../utils/validators';
import { SignupData, Person, OfficerType, validateOfficerRules } from 'form-application-shared';

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

// Relationship types enum
enum RelationshipType {
  BUSINESS_OWNER = 'Business Owner',
  BUSINESS_OWNER_SPOUSE = 'Business Owner Spouse',
  SIBLING = 'Sibling',
  PARENT_OR_RELATIVE = 'Parent or Relative',
  EMPLOYEE = 'Employee',
  INVESTOR = 'Investor',
  OTHER = 'Other'
}

// Helper component for tab panels
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`person-tabpanel-${index}`}
      aria-labelledby={`person-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

interface PeopleStepProps {
  formData: SignupData;
  setFormData: React.Dispatch<React.SetStateAction<SignupData>>;
  onValidationChange: (validationFn: () => boolean) => void;
}

interface PeopleErrors {
  general?: string;
  totalPercentage?: string;
  otherOwnershipDetails?: string;
  duplicateEmails?: string;
  duplicateNames?: string;
  noShareholders?: string;
  missingOfficers?: string;
  missingDirectors?: string;
  officerRules?: string;
  [key: string]: string | undefined;
}

const PeopleStep: React.FC<PeopleStepProps> = ({ 
  formData, 
  setFormData, 
  onValidationChange 
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [errors, setErrors] = useState<PeopleErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [totalPercentage, setTotalPercentage] = useState<number>(0);

  // Calculate total percentage owned
  useEffect(() => {
    const filledShareholders = formData.people.filter((p: Person) => 
      (p.legalName || p.email || p.taxId || p.addressLines || p.city || p.state || p.zip || p.percentOwned || p.dob) && p.isShareholder
    );

    const total = filledShareholders.reduce((sum: number, person: Person) => {
      return sum + (parseFloat((person.percentOwned ?? 0).toString()) || 0);
    }, 0);
    
    setTotalPercentage(parseFloat(total.toFixed(2)));
  }, [formData.people]);

  // Validation function
  const validateStep = useCallback(() => {
    let isValid = true;
    const newErrors: PeopleErrors = {};
    
    // Filter out completely empty entries
    const filledEntries = formData.people.filter((p: Person) => 
      p.legalName || p.email || p.taxId || p.addressLines || p.city || p.state || p.zip || p.percentOwned || p.dob
    );
    
    if (filledEntries.length === 0) {
      newErrors.general = 'At least one person must be provided';
      isValid = false;
    }
    
    // Check for at least one shareholder
    const shareholders = filledEntries.filter(p => p.isShareholder);
    if (shareholders.length === 0) {
      newErrors.noShareholders = 'At least one person must be marked as a shareholder';
      isValid = false;
    }
    
    // Check for required officers
    const hasPresident = filledEntries.some(p => p.officerTypes.includes(OfficerType.PRESIDENT));
    const hasSecretary = filledEntries.some(p => p.officerTypes.includes(OfficerType.SECRETARY));
    
    if (!hasPresident || !hasSecretary) {
      const missing = [];
      if (!hasPresident) missing.push('President');
      if (!hasSecretary) missing.push('Secretary');
      newErrors.missingOfficers = `The following officer positions are required: ${missing.join(', ')}`;
      isValid = false;
    }
    
    // Validate officer business rules
    const officerValidation = validateOfficerRules(filledEntries);
    if (!officerValidation.isValid) {
      newErrors.officerRules = officerValidation.errors.join('; ');
      isValid = false;
    }
    
    // Check for at least 2 directors
    const directors = filledEntries.filter(p => p.isDirector);
    if (directors.length < 2) {
      newErrors.missingDirectors = `At least 2 directors are required. Current count: ${directors.length}`;
      isValid = false;
    }
    
    // Check if total percentage equals 100% (only if there are shareholders)
    if (shareholders.length > 0 && Math.abs(totalPercentage - 100) > 0.01) {
      newErrors.totalPercentage = `Total percentage owned must equal 100%. Current total: ${totalPercentage}%`;
      isValid = false;
    }
    
    // Check for duplicate names across all entries
    const names = new Map<string, number[]>();
    const duplicateNames = new Set<string>();
    
    filledEntries.forEach((entry: Person, index: number) => {
      if (entry.legalName) {
        const normalizedName = entry.legalName.trim().toLowerCase();
        if (names.has(normalizedName)) {
          duplicateNames.add(entry.legalName);
          names.get(normalizedName)!.push(index);
        } else {
          names.set(normalizedName, [index]);
        }
      }
    });
    
    if (duplicateNames.size > 0) {
      newErrors.duplicateNames = `All people must have unique names. Duplicate name(s): ${Array.from(duplicateNames).join(', ')}`;
      isValid = false;
      
      // Mark duplicate name fields as errors
      names.forEach((indices, name) => {
        if (indices.length > 1) {
          indices.forEach(index => {
            newErrors[`people.${index}.legalName`] = 'Name must be unique';
          });
        }
      });
    }
    
    // Check for duplicate emails across all entries
    const emails = new Set<string>();
    const duplicateEmails = new Set<string>();
    
    filledEntries.forEach((entry: Person) => {
      if (entry.email) {
        if (emails.has(entry.email)) {
          duplicateEmails.add(entry.email);
        } else {
          emails.add(entry.email);
        }
      }
    });
    
    if (duplicateEmails.size > 0) {
      newErrors.duplicateEmails = `All people must have unique email addresses. Duplicate email(s): ${Array.from(duplicateEmails).join(', ')}`;
      isValid = false;
    }
    
    // Validate each entry
    filledEntries.forEach((entry: Person, index: number) => {
      if (!entry.legalName) {
        newErrors[`people.${index}.legalName`] = 'Legal name is required';
        isValid = false;
      }
      
      if (!entry.email) {
        newErrors[`people.${index}.email`] = 'Email is required';
        isValid = false;
      } else if (!isValidEmail(entry.email)) {
        newErrors[`people.${index}.email`] = 'Please enter a valid email address';
        isValid = false;
      } else if (duplicateEmails.has(entry.email)) {
        newErrors[`people.${index}.email`] = 'Email address must be unique';
        isValid = false;
      }
      
      if (!entry.taxId) {
        newErrors[`people.${index}.taxId`] = 'Tax ID is required';
        isValid = false;
      } else if (!isValidTaxId(entry.taxId)) {
        newErrors[`people.${index}.taxId`] = 'Please enter a valid tax ID (SSN: XXX-XX-XXXX or EIN: XX-XXXXXXX)';
        isValid = false;
      }
      
      if (!entry.addressLines) {
        newErrors[`people.${index}.addressLines`] = 'Address is required';
        isValid = false;
      }
      
      if (!entry.city) {
        newErrors[`people.${index}.city`] = 'City is required';
        isValid = false;
      }
      
      if (!entry.state) {
        newErrors[`people.${index}.state`] = 'State is required';
        isValid = false;
      }
      
      if (!entry.zip) {
        newErrors[`people.${index}.zip`] = 'ZIP code is required';
        isValid = false;
      } else if (!isValidZipCode(entry.zip)) {
        newErrors[`people.${index}.zip`] = 'Please enter a valid ZIP code';
        isValid = false;
      }
      
      // Validate DOB
      if (!entry.dob) {
        newErrors[`people.${index}.dob`] = 'Date of birth is required';
        isValid = false;
      } else if (!isValidDateOfBirth(entry.dob)) {
        newErrors[`people.${index}.dob`] = 'Date of birth must be in the past';
        isValid = false;
      }
      
      // Validate percentage owned only if marked as shareholder
      if (entry.isShareholder) {
        if (entry.percentOwned === 0 || entry.percentOwned === undefined || entry.percentOwned === null) {
          newErrors[`people.${index}.percentOwned`] = 'Percentage owned is required for shareholders';
          isValid = false;
        } else if (isNaN(parseFloat(entry.percentOwned.toString()))) {
          newErrors[`people.${index}.percentOwned`] = 'Percentage must be a number';
          isValid = false;
        } else if (parseFloat(entry.percentOwned.toString()) <= 0 || parseFloat(entry.percentOwned.toString()) > 100) {
          newErrors[`people.${index}.percentOwned`] = 'Percentage must be between 0.01 and 100';
          isValid = false;
        }
        
        // Validate relationship for shareholders
        if (!entry.relationship) {
          newErrors[`people.${index}.relationship`] = 'Relationship is required for shareholders';
          isValid = false;
        }
        
        // If relationship is "Other", validate otherRelationship
        if (entry.relationship === RelationshipType.OTHER && !entry.otherRelationship) {
          newErrors[`people.${index}.otherRelationship`] = 'Please specify the relationship';
          isValid = false;
        }
      }
    });
    
    // Check for ownership details if checkbox is checked
    if (formData.shareholdersHaveOtherOwnership && !formData.otherOwnershipDetails) {
      newErrors.otherOwnershipDetails = 'Please provide details about other ownership';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  }, [formData.people, formData.shareholdersHaveOtherOwnership, formData.otherOwnershipDetails, totalPercentage]);

  // Ensure active tab remains valid when entries change
  useEffect(() => {
    if (activeTab >= formData.people.length) {
      setActiveTab(formData.people.length - 1);
    }
  }, [formData.people.length, activeTab]);

  // Mark all fields as touched
  const markAllFieldsAsTouched = useCallback(() => {
    const allFields: Record<string, boolean> = {
      general: true,
      totalPercentage: true,
      otherOwnershipDetails: true,
      duplicateEmails: true,
      duplicateNames: true,
      noShareholders: true,
      missingOfficers: true,
      missingDirectors: true,
      officerRules: true
    };
    
    // Mark all entry fields as touched
    formData.people.forEach((_, index) => {
      allFields[`people.${index}.legalName`] = true;
      allFields[`people.${index}.email`] = true;
      allFields[`people.${index}.taxId`] = true;
      allFields[`people.${index}.addressLines`] = true;
      allFields[`people.${index}.city`] = true;
      allFields[`people.${index}.state`] = true;
      allFields[`people.${index}.zip`] = true;
      allFields[`people.${index}.dob`] = true;
      allFields[`people.${index}.percentOwned`] = true;
      allFields[`people.${index}.officerTypes`] = true;
      allFields[`people.${index}.isShareholder`] = true;
      allFields[`people.${index}.isDirector`] = true;
      allFields[`people.${index}.relationship`] = true;
      allFields[`people.${index}.otherRelationship`] = true;
    });
    
    setTouched(allFields);
  }, [formData.people]);

  // Create a validation wrapper that marks all fields as touched when validating
  const validateStepAndMarkTouched = useCallback(() => {
    markAllFieldsAsTouched();
    return validateStep();
  }, [validateStep, markAllFieldsAsTouched]);

  // Register validation function with parent component
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(validateStepAndMarkTouched);
    }
  }, [onValidationChange, validateStepAndMarkTouched]);

  // Validate on each change to provide real-time feedback
  useEffect(() => {
    validateStep();
  }, [validateStep]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue === formData.people.length) {
      // "Add Person" tab clicked
      addPerson();
      setActiveTab(newValue);
    } else {
      setActiveTab(newValue);
    }
  };

  const handleFieldChange = (index: number, name: keyof Person, value: string | number | null | string[]) => {
    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [`people.${index}.${name}`]: true
    }));
    
    setFormData(prev => {
      const entries = [...prev.people];
      const updatedEntry = { ...entries[index] };
      
      // Handle the field update
      if (name === 'percentOwned') {
        updatedEntry[name] = value === '' ? null : parseFloat(value as string);
      } else {
        updatedEntry[name] = value as any;
      }
      
      // Clear otherRelationship only if relationship changes from "Other" to something else
      if (name === 'relationship' && value !== RelationshipType.OTHER) {
        updatedEntry.otherRelationship = '';
      }
      
      entries[index] = updatedEntry;
      return { ...prev, people: entries };
    });
  };

  const handleCheckboxChange = (index: number, field: 'isShareholder' | 'isDirector') => (e: React.ChangeEvent<HTMLInputElement>) => {
    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [`people.${index}.${field}`]: true
    }));
    
    setFormData(prev => {
      const entries = [...prev.people];
      entries[index] = { 
        ...entries[index], 
        [field]: e.target.checked,
        // Clear percentage and relationship fields if unchecking shareholder
        percentOwned: field === 'isShareholder' && !e.target.checked ? null : entries[index].percentOwned,
        relationship: field === 'isShareholder' && !e.target.checked ? '' : entries[index].relationship,
        otherRelationship: field === 'isShareholder' && !e.target.checked ? '' : entries[index].otherRelationship
      };
      return { ...prev, people: entries };
    });
  };

  const addPerson = () => {
    const newEntry: Person = {
      legalName: "",
      email: "",
      taxId: "",
      addressLines: "",
      city: "",
      state: "",
      zip: "",
      percentOwned: null,
      dob: "",
      isShareholder: false,
      isDirector: false,
      officerTypes: [],
      relationship: "",
      otherRelationship: ""
    };
    
    setFormData(prev => ({
      ...prev,
      people: [...prev.people, newEntry]
    }));
  };

  const removePerson = (index: number) => {
    setFormData(prev => {
      const entries = [...prev.people];
      if (entries.length > 1) {
        entries.splice(index, 1);
      }
      return { ...prev, people: entries };
    });
  };

  // Handle checkbox and text box for additional ownership
  const handleOwnershipCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTouched(prev => ({
      ...prev,
      shareholdersHaveOtherOwnership: true
    }));
    
    setFormData(prev => ({
      ...prev,
      shareholdersHaveOtherOwnership: e.target.checked,
      otherOwnershipDetails: e.target.checked ? prev.otherOwnershipDetails : ""
    }));
  };

  const handleOwnershipDetailsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTouched(prev => ({
      ...prev,
      otherOwnershipDetails: true
    }));
    
    setFormData(prev => ({
      ...prev,
      otherOwnershipDetails: e.target.value
    }));
  };

  // Helper function to get person's roles as a string
  const getPersonRoles = (person: Person): string => {
    const roles = [];
    // Add officer types (excluding NONE)
    person.officerTypes.forEach(officerType => {
      if (officerType !== OfficerType.NONE) {
        roles.push(officerType);
      }
    });
    if (person.isShareholder) {
      roles.push('Shareholder');
    }
    if (person.isDirector) {
      roles.push('Director');
    }
    return roles.length > 0 ? roles.join(', ') : 'No roles assigned';
  };

  // Render form for a single person
  const renderPersonForm = (index: number) => {
    const entry: Person = formData.people[index];
    return (
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {/* Top row: Title and Delete button */}
          <Grid item xs={12} container justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">Person {index + 1}</Typography>
              <Typography variant="caption" color="textSecondary">
                {getPersonRoles(entry)}
              </Typography>
            </Box>
            <Tooltip title={formData.people.length === 1 ? "At least one person is required" : ""}>
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => removePerson(index)}
                  disabled={formData.people.length === 1}
                >
                  Delete Entry
                </Button>
              </span>
            </Tooltip>
          </Grid>
          
          {/* Row 1: Officer Types, Is Shareholder, Is Director */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Officer Types</InputLabel>
              <Select
                multiple
                value={entry.officerTypes || []}
                onChange={(e) => {
                  const value = e.target.value as string[];
                  handleFieldChange(index, 'officerTypes', value);
                }}
                input={<OutlinedInput label="Officer Types" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                <MenuItem value={OfficerType.PRESIDENT}>President</MenuItem>
                <MenuItem value={OfficerType.SECRETARY}>Secretary</MenuItem>
                <MenuItem value={OfficerType.TREASURER}>Treasurer</MenuItem>
                <MenuItem value={OfficerType.VICE_PRESIDENT}>Vice President</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={entry.isDirector || false}
                  onChange={handleCheckboxChange(index, 'isDirector')}
                />
              }
              label="Director"
            />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={entry.isShareholder || false}
                  onChange={handleCheckboxChange(index, 'isShareholder')}
                />
              }
              label="Shareholder"
            />
          </Grid>
          
          {/* Row 2: Shareholder fields (only shown when isShareholder is true) */}
          {entry.isShareholder && (
            <>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Percentage Owned"
                  name="percentOwned"
                  type="number"
                  value={entry.percentOwned || ''}
                  onChange={(e) => handleFieldChange(index, 'percentOwned', e.target.value)}
                  fullWidth
                  required
                  inputProps={{ step: 0.01, min: 0.01, max: 100 }}
                  error={touched[`people.${index}.percentOwned`] && !!errors[`people.${index}.percentOwned`]}
                  helperText={touched[`people.${index}.percentOwned`] && errors[`people.${index}.percentOwned`]}
                />
              </Grid>
              <Grid item xs={12} sm={entry.relationship === RelationshipType.OTHER ? 4 : 8}>
                <FormControl fullWidth required>
                  <InputLabel>Relationship to Producer(s)</InputLabel>
                  <Select
                    name="relationship"
                    value={entry.relationship || ''}
                    onChange={(e) => handleFieldChange(index, 'relationship', e.target.value)}
                    label="Relationship to Producer(s)"
                    error={touched[`people.${index}.relationship`] && !!errors[`people.${index}.relationship`]}
                  >
                    <MenuItem value="">Select...</MenuItem>
                    <MenuItem value={RelationshipType.BUSINESS_OWNER}>Business Owner</MenuItem>
                    <MenuItem value={RelationshipType.BUSINESS_OWNER_SPOUSE}>Business Owner Spouse</MenuItem>
                    <MenuItem value={RelationshipType.SIBLING}>Sibling</MenuItem>
                    <MenuItem value={RelationshipType.PARENT_OR_RELATIVE}>Parent or Relative</MenuItem>
                    <MenuItem value={RelationshipType.EMPLOYEE}>Employee</MenuItem>
                    <MenuItem value={RelationshipType.INVESTOR}>Investor</MenuItem>
                    <MenuItem value={RelationshipType.OTHER}>Other</MenuItem>
                  </Select>
                  {touched[`people.${index}.relationship`] && errors[`people.${index}.relationship`] && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors[`people.${index}.relationship`]}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              {entry.relationship === RelationshipType.OTHER && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Specify Relationship"
                    name="otherRelationship"
                    value={entry.otherRelationship || ''}
                    onChange={(e) => handleFieldChange(index, 'otherRelationship', e.target.value)}
                    fullWidth
                    required
                    error={touched[`people.${index}.otherRelationship`] && !!errors[`people.${index}.otherRelationship`]}
                    helperText={touched[`people.${index}.otherRelationship`] && errors[`people.${index}.otherRelationship`]}
                  />
                </Grid>
              )}
            </>
          )}
          
          {/* Row 3: Legal Name */}
          <Grid item xs={12}>
            <TextField
              label="Legal Name"
              name="legalName"
              value={entry.legalName}
              onChange={(e) => handleFieldChange(index, 'legalName', e.target.value)}
              fullWidth
              required
              error={touched[`people.${index}.legalName`] && !!errors[`people.${index}.legalName`]}
              helperText={touched[`people.${index}.legalName`] && errors[`people.${index}.legalName`]}
            />
          </Grid>
          
          {/* Row 4: Email and Tax ID */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              name="email"
              type="email"
              value={entry.email}
              onChange={(e) => handleFieldChange(index, 'email', e.target.value)}
              fullWidth
              required
              error={touched[`people.${index}.email`] && !!errors[`people.${index}.email`]}
              helperText={touched[`people.${index}.email`] && errors[`people.${index}.email`]}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Tax ID"
              name="taxId"
              value={entry.taxId}
              onChange={(e) => handleFieldChange(index, 'taxId', e.target.value)}
              fullWidth
              required
              error={touched[`people.${index}.taxId`] && !!errors[`people.${index}.taxId`]}
              helperText={touched[`people.${index}.taxId`] && errors[`people.${index}.taxId`]}
            />
          </Grid>
          
          {/* Row 5: Address */}
          <Grid item xs={12}>
            <TextField
              label="Address"
              name="addressLines"
              value={entry.addressLines}
              onChange={(e) => handleFieldChange(index, 'addressLines', e.target.value)}
              fullWidth
              required
              error={touched[`people.${index}.addressLines`] && !!errors[`people.${index}.addressLines`]}
              helperText={touched[`people.${index}.addressLines`] && errors[`people.${index}.addressLines`]}
            />
          </Grid>
          
          {/* Row 6: City, State, Zip */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="City"
              name="city"
              value={entry.city}
              onChange={(e) => handleFieldChange(index, 'city', e.target.value)}
              fullWidth
              required
              error={touched[`people.${index}.city`] && !!errors[`people.${index}.city`]}
              helperText={touched[`people.${index}.city`] && errors[`people.${index}.city`]}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label="State"
              name="state"
              value={entry.state}
              onChange={(e) => handleFieldChange(index, 'state', e.target.value)}
              fullWidth
              required
              error={touched[`people.${index}.state`] && !!errors[`people.${index}.state`]}
              helperText={touched[`people.${index}.state`] && errors[`people.${index}.state`]}
            >
              {STATES.map(({ code, name }) => (
                <MenuItem key={code} value={code}>
                  {code} - {name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Zip"
              name="zip"
              value={entry.zip}
              onChange={(e) => handleFieldChange(index, 'zip', e.target.value)}
              fullWidth
              required
              error={touched[`people.${index}.zip`] && !!errors[`people.${index}.zip`]}
              helperText={touched[`people.${index}.zip`] && errors[`people.${index}.zip`]}
            />
          </Grid>
          
          {/* Row 7: DOB */}
          <Grid item xs={12}>
            <TextField
              label="Date of Birth"
              name="dob"
              type="date"
              value={entry.dob}
              onChange={(e) => handleFieldChange(index, 'dob', e.target.value)}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().split('T')[0] }}
              error={touched[`people.${index}.dob`] && !!errors[`people.${index}.dob`]}
              helperText={touched[`people.${index}.dob`] && errors[`people.${index}.dob`]}
            />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  return (
    <>
      <Typography variant="h5" gutterBottom>
        Officers, Directors & Shareholders
      </Typography>

      {/* General error messages */}
      {touched.general && errors.general && (
        <Alert severity="error" sx={{ mb: 2 }}>{errors.general}</Alert>
      )}
      
      {/* Missing officers error */}
      {touched.missingOfficers && errors.missingOfficers && (
        <Alert severity="error" sx={{ mb: 2 }}>{errors.missingOfficers}</Alert>
      )}
      
      {/* Officer rules validation error */}
      {touched.officerRules && errors.officerRules && (
        <Alert severity="error" sx={{ mb: 2 }}>{errors.officerRules}</Alert>
      )}
      
      {/* Missing directors error */}
      {touched.missingDirectors && errors.missingDirectors && (
        <Alert severity="error" sx={{ mb: 2 }}>{errors.missingDirectors}</Alert>
      )}
      
      {/* No shareholders error */}
      {touched.noShareholders && errors.noShareholders && (
        <Alert severity="error" sx={{ mb: 2 }}>{errors.noShareholders}</Alert>
      )}
      
      {/* Total percentage error */}
      {touched.totalPercentage && errors.totalPercentage && (
        <Alert severity="error" sx={{ mb: 2 }}>{errors.totalPercentage}</Alert>
      )}
      
      {/* Duplicate names error */}
      {touched.duplicateNames && errors.duplicateNames && (
        <Alert severity="error" sx={{ mb: 2 }}>{errors.duplicateNames}</Alert>
      )}
      
      {/* Duplicate email error */}
      {touched.duplicateEmails && errors.duplicateEmails && (
        <Alert severity="error" sx={{ mb: 2 }}>{errors.duplicateEmails}</Alert>
      )}
      
      {/* Total percentage indicator */}
      <Alert 
        severity={Math.abs(totalPercentage - 100) < 0.01 || totalPercentage === 0 ? "success" : "info"} 
        sx={{ mb: 2 }}
      >
        Total ownership: {totalPercentage}% {totalPercentage === 0 ? "(No shareholders selected)" : Math.abs(totalPercentage - 100) < 0.01 ? "(Valid)" : "(Must equal 100%)"}
      </Alert>

      <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
        {formData.people.map((_, index) => (
          <Tab key={index} label={`Person ${index + 1}`} />
        ))}
        <Tab label="Add Person" />
      </Tabs>

      {formData.people.map((_, index) => (
        <TabPanel value={activeTab} index={index} key={index}>
          {renderPersonForm(index)}
        </TabPanel>
      ))}

      {/* Ownership Checkbox */}
      <FormControlLabel
        control={
          <Checkbox
            checked={formData.shareholdersHaveOtherOwnership || false}
            onChange={handleOwnershipCheckboxChange}
          />
        }
        label="Do any of the shareholders have ownership in any other insurance or reinsurance company(ies)?"
      />

      {/* Ownership Details Text Field (Shown only when checkbox is checked) */}
      {formData.shareholdersHaveOtherOwnership && (
        <TextField
          label="Please provide details"
          name="otherOwnershipDetails"
          value={formData.otherOwnershipDetails || ""}
          onChange={handleOwnershipDetailsChange}
          fullWidth
          multiline
          rows={3}
          sx={{ mt: 2 }}
          required
          error={touched.otherOwnershipDetails && !!errors.otherOwnershipDetails}
          helperText={touched.otherOwnershipDetails && errors.otherOwnershipDetails}
        />
      )}
    </>
  );
};

export default PeopleStep;